/**
 * NodeUploadService.js
 *
 * Manages file uploads for node contributions.
 * Handles queuing, progress tracking, and persistence.
 */

// Firebase
import { uploadBytesResumable, ref } from 'firebase/storage';
import { Timestamp, doc, getDoc } from 'firebase/firestore';
import { db, collections, storage } from 'firebaseConfig';

// Utilities
import { EngagementType } from 'utilities/Enums';
import { generateKey } from 'utilities/Keys';
import { getSingularLabel } from 'utilities/Languages';
import { enrichItemMetadataFromUrl, generateSecureFolder } from 'utilities/NodeUploadHelpers';
import { splitNodeSecuredFields } from 'utilities/NodeSecurity';
import { detectFileNodeType } from 'utilities/NodeType';
import { getNodeType } from 'services/nodetypes/NodeTypesCacheService';
import { buildUserSummary } from 'utilities/Users';

// Services
import { createEngagement } from 'services/engagements/EngagementWriteService';
import { prepareWorkspaceUploadIntent } from 'services/storage/StorageUploadIntentService';
import { assertWorkspaceUploadAllowed } from 'services/workspaces/WorkspaceUploadPolicyService';
import { upsertEdge } from './NodeEdgeService';
import { patchNode, setNodeDocs, setNodeSecuredDocs, updateNode } from 'services/nodes/NodeWriteService';

// Data URL pattern for base64 extraction
const dataUrlPattern = /^data:(.+);base64,(.*)$/;

// atob function for base64 decoding
const atobFn = typeof atob === 'function'
  ? atob
  : (typeof window !== 'undefined' && typeof window.atob === 'function' ? window.atob.bind(window) : null);

  /**
   * Sanitizes a file name by removing special characters.
   * Replaces spaces and unsafe characters with underscores.
   * 
   * @param {string} name - The original file name.
   * 
   * @returns {string} The sanitized file name.
   */
const sanitizeFileName = (name = '') => {
  return name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]/g, '_');
};

/**
 * Gets the file extension from a file object or fallback type.
 * 
 * @param {Object} file - The file object.
 * @param {string} [fallbackType] - The fallback type if file info is insufficient.
 * 
 * @returns {string} The file extension (without dot).
 */
const getFileExtension = (file, fallbackType = 'file') => {

  // Try to extract from file name
  if (file?.name && file.name.includes('.')) {
    return file.name.split('.').pop().toLowerCase();
  }

  // Try to extract from MIME type
  if (file?.type && file.type.includes('/')) {
    return file.type.split('/').pop().toLowerCase();
  }

  // Fallback based on provided type
  if (typeof fallbackType === 'string' && fallbackType.includes('/')) {
    return fallbackType.split('/').pop().toLowerCase();
  }

  // Generic fallback mapping
  const defaultMap = {
    photo: 'jpg',
    image: 'jpg',
    video: 'mp4',
  };

  // Return mapped extension or 'bin'
  return defaultMap[fallbackType.toLowerCase()] || 'bin';
};

/**
 * Builds upload metadata for Firebase Storage.
 * 
 * @param {string} itemKey - The item key.
 * @param {string} nodeKey - The node key.
 * @param {string} userKey - The user key.
 * @param {string} workspaceKey - The workspace key.
 * @param {string} [contentType] - Optional content type.
 * 
 * @returns {Object} Metadata object for upload.
 */
const buildUploadMetadata = ({ itemKey, nodeKey, userKey, workspaceKey, contentType, intentId }) => {

  // Construct metadata object
  const metadata = {
    customMetadata: {
      itemKey: itemKey || '',
      intentId: intentId || '',
      nodeKey: nodeKey || '',
      userKey: userKey || '',
      workspaceKey: workspaceKey || '',
    },
  };

  // Add content type if provided
  if (contentType) {
    metadata.contentType = contentType;
  }

  // Return the constructed metadata object
  return metadata;
};

async function prepareUploadIntent({
  contentType,
  fileSizeBytes,
  itemKey,
  nodeKey,
  storagePath,
  uploadKind,
  workspaceKey,
}) {
  const cleanedWorkspaceKey = String(workspaceKey || '').trim();
  const cleanedStoragePath = String(storagePath || '').trim();
  if (!cleanedWorkspaceKey || !cleanedStoragePath) {
    return null;
  }

  const result = await prepareWorkspaceUploadIntent({
    contentType,
    fileSizeBytes,
    itemKey,
    nodeKey,
    storagePath: cleanedStoragePath,
    uploadKind,
    workspaceKey: cleanedWorkspaceKey,
  });

  return String(result?.intentId || '').trim() || null;
}

/**
 * Resolves a Blob from various source file representations.
 * 
 * @param {Object|Blob} sourceFile - The source file representation.
 * 
 * @returns {Promise<Blob|null>} Resolves to a Blob or null if unable.
 */
const resolveBlobFromSource = async (sourceFile) => {

  // Handle null/undefined
  if (!sourceFile) return null;

  // If already a Blob, return it
  if (sourceFile instanceof Blob) {
    return sourceFile;
  }

  // If has localFile as Blob, return it
  if (sourceFile?.localFile instanceof Blob) {
    return sourceFile.localFile;
  }

  // Try to resolve from URI or base64
  const candidateUri = sourceFile?.localUri || sourceFile?.uri;

  // Handle data URLs and remote URIs
  if (typeof candidateUri === 'string') {

    // Fetch blob for blob: or http(s): URIs
    if (candidateUri.startsWith('blob:') || candidateUri.startsWith('http')) {
      try {
        const blob = await fetch(candidateUri).then(res => res.blob());
        if (blob && blob.size > 0) {
          return blob;
        }
      } catch (err) {
        console.warn('NodeUploadService: Failed to fetch blob from uri', candidateUri, err);
      }
    }

    // Handle data URI
    const dataUrlMatch = candidateUri.match(dataUrlPattern);

    // Decode base64 data URI
    if (dataUrlMatch && atobFn) {
      try {
        const mimeType = dataUrlMatch[1];
        const base64 = dataUrlMatch[2];
        const byteCharacters = atobFn(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: mimeType });
      } catch (decodeErr) {
        console.warn('NodeUploadService: Failed to decode data URI for upload', decodeErr);
      }
    }
  }

  // Handle raw base64 data
  if (typeof sourceFile?.base64Data === 'string' && atobFn) {
    try {
      const base64 = sourceFile.base64Data;
      const mimeType = sourceFile.type || 'application/octet-stream';
      const byteCharacters = atobFn(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type: mimeType });
    } catch (base64Err) {
      console.warn('NodeUploadService: Failed to decode base64 payload', base64Err);
    }
  }

  return null;
};

/**
 * NodeUploadService class
 * 
 * Manages file uploads for node contributions.
 */
class NodeUploadService {

  // Singleton instance
  constructor() {
    this.activeUploads = new Map();
    this.completedUploads = new Map();
    this.uploadQueue = [];
    this.listeners = new Set();
    this.maxConcurrent = 1; // Reduced from 2 to eliminate concurrency issues
  }

  // Subscribe to upload status changes
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // Notify all listeners of status changes
  notifyListeners() {
    this.listeners.forEach(listener => listener({
      activeUploads: Array.from(this.activeUploads.values()),
      completedUploads: Array.from(this.completedUploads.values())
    }));
  }

  // Queue a contribution upload
  queueContribution(files, node, user, onComplete, onError) {
    
    // Check if there's already an active contribution upload for this node
    const existingJob = Array.from(this.activeUploads.values()).find(
      job => job.type === 'contribution' && job.node?.key === node?.key
    );

    // Check for duplicate active contribution upload for this node
    if (existingJob) {
      return null; // Return null to indicate duplicate was ignored
    }

    // Create unique upload ID
    const uploadId = generateKey();

    // Create upload job
    const uploadJob = {
      id: uploadId,
      type: 'contribution',
      files,
      node,
      user,
      onComplete,
      onError,
      status: 'queued',
      progress: 0,
      dateCreated: Date.now()
    };

    // Add to upload queue
    this.uploadQueue.push(uploadJob);

    // Don't add to activeUploads yet - wait until we start processing
    this.notifyListeners();

    // Process the upload queue
    this.processQueue();

    // Return the upload ID
    return uploadId;
  }

  // Queue a single-node media upload
  queueNodeUpload(file, node, user, onComplete, onError) {
    if (!file || !node?.key || !user?.key) return null;

    const uploadId = generateKey();

    const uploadJob = {
      id: uploadId,
      type: 'node-upload',
      file,
      node,
      user,
      onComplete,
      onError,
      status: 'queued',
      progress: 0,
      totalFiles: 1,
      completedFiles: 0,
      dateCreated: Date.now(),
    };

    this.uploadQueue.push(uploadJob);
    this.notifyListeners();
    this.processQueue();

    return uploadId;
  }

  // Queue a batch of single-node uploads (one indicator)
  queueNodeUploads(entries, user, onComplete, onError) {
    if (!Array.isArray(entries) || entries.length === 0 || !user?.key) return null;

    const normalized = entries
      .filter((entry) => entry?.file && entry?.node?.key)
      .map((entry) => ({ file: entry.file, node: entry.node }));

    if (normalized.length === 0) return null;

    const uploadId = generateKey();

    const uploadJob = {
      id: uploadId,
      type: 'node-upload-batch',
      entries: normalized,
      user,
      onComplete,
      onError,
      status: 'queued',
      progress: 0,
      totalFiles: normalized.length,
      completedFiles: 0,
      dateCreated: Date.now(),
    };

    this.uploadQueue.push(uploadJob);
    this.notifyListeners();
    this.processQueue();

    return uploadId;
  }

  // Process the upload queue
  async processQueue() {

    // Start uploads while under max concurrency
    while (this.uploadQueue.length > 0 && this.activeUploads.size < this.maxConcurrent) {

      // Dequeue next job
      const job = this.uploadQueue.shift();

      // Add to activeUploads when we start processing
      this.activeUploads.set(job.id, job);

      // Start the upload
      if (job.type === 'node-upload') {
        this.startNodeUpload(job);
      } else if (job.type === 'node-upload-batch') {
        this.startNodeUploadBatch(job);
      } else {
        this.startContributionUpload(job);
      }
    }
  }

  async uploadNodeFile({ file, node, user, onProgress }) {
    const uploadBlob = await resolveBlobFromSource(file);
    if (!uploadBlob || uploadBlob.size === 0) {
      throw new Error('Unable to read file for upload.');
    }

    await assertWorkspaceUploadAllowed({
      workspaceKey: node?.workspaceKey,
      fileName: file?.name || node?.fileName || node?.title,
      fileSizeBytes: uploadBlob.size,
    });

    const typeDef = getNodeType(node?.type);
    const primitiveKey = (typeDef?.primitiveKey || node?.type || detectFileNodeType(file?.type) || 'FILE').toUpperCase();

    const secureFolder = node?.secureFolder || generateSecureFolder();
    const basePath = `media/${secureFolder}/${node.key}`;

    let storagePath = null;
    let url = null;
    let url_thumb = null;
    let url_grid = null;
    let url_feed = null;
    let data_document = node?.data_document ? { ...node.data_document } : null;
    let data_file = node?.data_file ? { ...node.data_file } : null;
    let intentId = null;

    const extension = getFileExtension(file, uploadBlob.type || primitiveKey);

    if (primitiveKey === 'PHOTO') {
      const photoPath = `${basePath}/original.${extension}`;
      intentId = await prepareUploadIntent({
        contentType: uploadBlob.type,
        fileSizeBytes: uploadBlob.size,
        itemKey: node.key,
        nodeKey: node.key,
        storagePath: photoPath,
        uploadKind: 'node-upload',
        workspaceKey: node?.workspaceKey || '',
      });
      const photoRef = ref(storage, photoPath);
      await this.uploadFile(photoRef, uploadBlob, onProgress, buildUploadMetadata({
        contentType: uploadBlob.type,
        intentId,
        itemKey: node.key,
        nodeKey: node.key,
        userKey: user.key,
        workspaceKey: node?.workspaceKey || '',
      }));
      storagePath = photoPath;
      url = photoPath;
      url_thumb = `${basePath}/thumb_200.${extension}`;
      url_grid = `${basePath}/thumb_400.${extension}`;
      url_feed = `${basePath}/thumb_1080.${extension}`;
    } else if (primitiveKey === 'VIDEO') {
      const videoPath = `${basePath}/original.${extension}`;
      intentId = await prepareUploadIntent({
        contentType: uploadBlob.type,
        fileSizeBytes: uploadBlob.size,
        itemKey: node.key,
        nodeKey: node.key,
        storagePath: videoPath,
        uploadKind: 'node-upload',
        workspaceKey: node?.workspaceKey || '',
      });
      const videoRef = ref(storage, videoPath);
      await this.uploadFile(videoRef, uploadBlob, onProgress, buildUploadMetadata({
        contentType: uploadBlob.type,
        intentId,
        itemKey: node.key,
        nodeKey: node.key,
        userKey: user.key,
        workspaceKey: node?.workspaceKey || '',
      }));
      storagePath = videoPath;
      url = videoPath;
    } else if (primitiveKey === 'DOCUMENT') {
      const docExtension = file?.name ? file.name.split('.').pop() : extension;
      const docPath = `${basePath}/original.${docExtension}`;
      intentId = await prepareUploadIntent({
        contentType: uploadBlob.type,
        fileSizeBytes: uploadBlob.size,
        itemKey: node.key,
        nodeKey: node.key,
        storagePath: docPath,
        uploadKind: 'node-upload',
        workspaceKey: node?.workspaceKey || '',
      });
      const docRef = ref(storage, docPath);
      await this.uploadFile(docRef, uploadBlob, onProgress, buildUploadMetadata({
        contentType: uploadBlob.type,
        intentId,
        itemKey: node.key,
        nodeKey: node.key,
        userKey: user.key,
        workspaceKey: node?.workspaceKey || '',
      }));
      storagePath = docPath;
      const nextDataDocument = { ...(data_document || {}) };
      nextDataDocument.securedUrl = docPath;
      data_document = nextDataDocument;
    } else {
      const fileExtension = file?.name ? file.name.split('.').pop() : extension;
      const filePath = `${basePath}/original.${fileExtension}`;
      intentId = await prepareUploadIntent({
        contentType: uploadBlob.type,
        fileSizeBytes: uploadBlob.size,
        itemKey: node.key,
        nodeKey: node.key,
        storagePath: filePath,
        uploadKind: 'node-upload',
        workspaceKey: node?.workspaceKey || '',
      });
      const fileRef = ref(storage, filePath);
      await this.uploadFile(fileRef, uploadBlob, onProgress, buildUploadMetadata({
        contentType: uploadBlob.type,
        intentId,
        itemKey: node.key,
        nodeKey: node.key,
        userKey: user.key,
        workspaceKey: node?.workspaceKey || '',
      }));
      storagePath = filePath;
      const nextDataFile = { ...(data_file || {}) };
      nextDataFile.securedUrl = filePath;
      data_file = nextDataFile;
    }

    const patch = {
      secureFolder,
      storagePath,
      ...(url ? { url } : {}),
      ...(url_thumb ? { url_thumb } : {}),
      ...(url_grid ? { url_grid } : {}),
      ...(url_feed ? { url_feed } : {}),
      ...(data_document ? { data_document } : {}),
      ...(data_file ? { data_file } : {}),
      fileName: node?.fileName || file?.name || null,
    };

    await updateNode(node.key, patch, { actor: user });

    return patch;
  }

  // Start uploading a single-node media file
  async startNodeUpload(job) {
    const { file, node, user, id } = job;

    try {
      job.status = 'uploading';
      job.totalFiles = 1;
      job.completedFiles = 0;
      job.progress = 0;
      this.notifyListeners();

      const updateProgress = (progress) => {
        job.progress = progress;
        this.notifyListeners();
      };

      const patch = await this.uploadNodeFile({ file, node, user, onProgress: updateProgress });

      job.completedFiles = 1;
      job.progress = 100;
      job.status = 'completed';
      job.completedAt = Date.now();

      this.completedUploads.set(id, job);
      this.activeUploads.delete(id);
      this.notifyListeners();

      job.onComplete?.(patch);
    } catch (error) {
      console.error('NodeUploadService: Node upload failed', error);
      job.status = 'error';
      job.error = error;
      job.completedAt = Date.now();
      this.completedUploads.set(id, job);
      this.activeUploads.delete(id);
      this.notifyListeners();
      job.onError?.(error);
    }

    this.processQueue();
  }

  async startNodeUploadBatch(job) {
    const { entries, user, id } = job;

    try {
      job.status = 'uploading';
      job.totalFiles = Array.isArray(entries) ? entries.length : 0;
      job.completedFiles = 0;
      job.progress = 0;
      this.notifyListeners();

      const patches = [];

      for (let i = 0; i < job.totalFiles; i += 1) {
        const entry = entries[i];
        if (!entry?.file || !entry?.node) {
          job.completedFiles += 1;
          this.notifyListeners();
          continue;
        }

        const updateProgress = (progress) => {
          job.progress = ((job.completedFiles + progress / 100) / Math.max(job.totalFiles, 1)) * 100;
          this.notifyListeners();
        };

        const patch = await this.uploadNodeFile({
          file: entry.file,
          node: entry.node,
          user,
          onProgress: updateProgress,
        });
        patches.push({ nodeKey: entry.node.key, patch });

        job.completedFiles += 1;
        this.notifyListeners();
      }

      job.progress = 100;
      job.status = 'completed';
      job.completedAt = Date.now();

      this.completedUploads.set(id, job);
      this.activeUploads.delete(id);
      this.notifyListeners();

      job.onComplete?.(patches);
    } catch (error) {
      console.error('NodeUploadService: Batch node upload failed', error);
      job.status = 'error';
      job.error = error;
      job.completedAt = Date.now();

      this.completedUploads.set(id, job);
      this.activeUploads.delete(id);
      this.notifyListeners();

      job.onError?.(error);
    }

    this.processQueue();
  }

  // Start uploading a contribution
  async startContributionUpload(job) {

    // Destructure job details
    const { files, node, user, id } = job;

    try {

      // Update job status
      job.status = 'preparing';

      // Notify listeners of status change
      this.notifyListeners();

      // Generate secure folder for this upload batch
      const secureFolder = generateSecureFolder();

      // Prepare items (same logic as handleSelectComplete)
      const now = Timestamp.now();

      // Ensure workspace key is present
      if (!node?.workspaceKey) {
        throw new Error('NodeUploadService: missing workspaceKey on parent node');
      }

      // Create new items for each file
      const newItems = await Promise.all(files.map(async (file, index) => {

        // Detect node type
        const type = detectFileNodeType(file.type);

        // Build base item
        let title = null;
        let data_document = null;
        let data_file = null;

        // Extract metadata for DOCUMENT or FILE types
        if ((type === 'DOCUMENT' || type === 'FILE') && file.name) {

          // Use file name as title and build metadata
          const fileName = file.name;
          const extension = fileName.split('.').pop()?.toLowerCase() || '';
          const baseName = fileName.replace(/\.[^/.]+$/, '');

          title = baseName;

          const metadata = {
            filename: fileName,
            size: file.size,
            mimeType: file.type,
            lastModified: file.lastModified || null,
            extension,
          };

          // Assign to appropriate data field
          if (type === 'DOCUMENT') {
            data_document = metadata;
          } else {
            data_file = metadata;
          }
        }

        if (!title) {
          const typeDef = getNodeType(type);
          const singularLabel = getSingularLabel(typeDef, type, 'en');
          title = `New ${singularLabel}`;
        }

        return {
          key: generateKey(),
          type,
          title,
          data_document,
          data_file,
          userKey: user.key,
          user: buildUserSummary(user),
          workspaceKey: node.workspaceKey,
          url: file.uri,
          base64Data: file.base64Data || null,
          localUri: file.localUri || null,
          fileName: file.name,
          contributorKey: user.key,
          contributorUser: buildUserSummary(user),
          dateCreated: now,
          dateModified: now,
          secureFolder,
          _sourceFileIndex: index,
        };
      }));

      job.status = 'uploading';
      job.totalFiles = newItems.length;
      job.completedFiles = 0;
      job.progress = 0;
      this.notifyListeners();

      // Upload each file sequentially
      const uploadedItems = [];

      // Iterate over new items to upload
      for (let i = 0; i < newItems.length; i++) {

        // Get current item and corresponding source file
        const item = newItems[i];
        const sourceFile = files[item._sourceFileIndex] || files[i];

        // Resolve Blob for upload
        const uploadBlob = await resolveBlobFromSource(sourceFile);

        // Handle missing blob
        if (!uploadBlob || uploadBlob.size === 0) {
          job.completedFiles++;
          this.notifyListeners();
          continue;
        }

        await assertWorkspaceUploadAllowed({
          workspaceKey: node?.workspaceKey,
          fileName: sourceFile?.name || item?.fileName || item?.title,
          fileSizeBytes: uploadBlob.size,
        });

        // Determine content type
        const resolvedContentType = uploadBlob.type || sourceFile?.type || item?.mimeType || '';

        // Function to update progress
        const updateProgress = (progress) => {

          // Update job progress
          job.progress = ((job.completedFiles + progress / 100) / Math.max(job.totalFiles, 1)) * 100;

          // Notify listeners of progress update
          this.notifyListeners();
        };

        try {
          let metadata = null;

          // Determine storage path and upload
          if (item.type === 'PHOTO' || item.type === 'VIDEO') {

            // Use structured path for media types
            const extension = getFileExtension(sourceFile, (item.type || resolvedContentType || 'file'));
            const basePath = `media/${secureFolder}/${node.key}/${item.key}`;
            const originalPath = `${basePath}/original.${extension}`;
            const intentId = await prepareUploadIntent({
              contentType: resolvedContentType,
              fileSizeBytes: uploadBlob.size,
              itemKey: item.key,
              nodeKey: node?.key || '',
              storagePath: originalPath,
              uploadKind: 'node-contribution',
              workspaceKey: node?.workspaceKey || '',
            });
            metadata = buildUploadMetadata({
              contentType: resolvedContentType,
              intentId,
              itemKey: item.key,
              nodeKey: node?.key || '',
              userKey: user?.key || '',
              workspaceKey: node?.workspaceKey || '',
            });

            // Create storage reference
            const storageRef = ref(storage, originalPath);

            // Upload the file
            const fileUrl = await this.uploadFile(storageRef, uploadBlob, updateProgress, metadata);
            
            // Persist object paths (Storage reads are locked down; URLs are obtained via MediaGate).
            item.storagePath = fileUrl;
            item.url = fileUrl;

            // Generate derivative paths
            if (item.type === 'PHOTO') {

              // Photo derivatives
              item.url_thumb = `${basePath}/thumb_200.${extension}`;
              item.url_grid = `${basePath}/thumb_400.${extension}`;
              item.url_feed = `${basePath}/thumb_1080.${extension}`;

              // Attempt to enrich metadata from URL
              try {
                if (typeof fileUrl === 'string' && fileUrl.startsWith('http')) {
                  await enrichItemMetadataFromUrl(fileUrl, item);
                }
              } catch (metadataError) {
                console.warn('NodeUploadService: Failed to enrich metadata for item', item.key, metadataError);
              }
            }
          } else {

            // Generic file upload
            const fallbackType = item.type || 'file';
            const sanitizedName = sanitizeFileName(sourceFile?.name || `${fallbackType.toLowerCase()}_${item.key}`);
            const timestamp = Date.now();
            const filePath = `uploads/${timestamp}_${item.key}_${sanitizedName}`;
            const intentId = await prepareUploadIntent({
              contentType: resolvedContentType,
              fileSizeBytes: uploadBlob.size,
              itemKey: item.key,
              nodeKey: node?.key || '',
              storagePath: filePath,
              uploadKind: 'node-contribution',
              workspaceKey: node?.workspaceKey || '',
            });
            metadata = buildUploadMetadata({
              contentType: resolvedContentType,
              intentId,
              itemKey: item.key,
              nodeKey: node?.key || '',
              userKey: user?.key || '',
              workspaceKey: node?.workspaceKey || '',
            });

            // Create storage reference
            const storageRef = ref(storage, filePath);

            // Upload the file
            const fileUrl = await this.uploadFile(storageRef, uploadBlob, updateProgress, metadata);

            // Persist object paths
            item.storagePath = filePath;

            // For DOCUMENT contributions created via NodeUploadService, store
            // the URL in the schema-secured field; other types can keep url.
            if (item.type === 'DOCUMENT') {

              // Move URL into data_document.securedUrl
              const nextDataDocument = { ...(item.data_document || {}) };
              nextDataDocument.securedUrl = fileUrl;
              item.data_document = nextDataDocument;
              delete item.url;
            } else {
              item.url = fileUrl;
            }
          }
        } catch (uploadError) {
          console.error(`NodeUploadService: Upload failed for item ${item.key}`, uploadError);
          throw uploadError;
        }

        // Add to uploaded items
        uploadedItems.push(item);

        // Update job progress
        job.completedFiles++;
        
        // Notify listeners of progress
        this.notifyListeners();
      }

      // Remove internal-only fields before persisting
      const persistedItems = uploadedItems.map(({ _sourceFileIndex, secureFolder: _secure, ...rest }) => rest);

      // Update job status
      job.status = 'saving';

      // Notify listeners of status change
      this.notifyListeners();

      // Persist contribution nodes and secured payloads
      await this.saveContributions(persistedItems, node.key, user, now);

      // Create engagement for contribution
      job.status = 'completed';
      job.completedAt = Date.now();
      job.progress = 100;

      // Record engagement
      this.completedUploads.set(id, job);

      // Remove from active uploads
      this.activeUploads.delete(id);

      // Clean up old completed uploads (keep last 10)
      if (this.completedUploads.size > 10) {

        // Find oldest entry
        const oldest = Array.from(this.completedUploads.keys())[0];

        // Delete the oldest entry
        this.completedUploads.delete(oldest);
      }

      // Notify listeners of completion
      this.notifyListeners();

      // Invoke completion callback
      job.onComplete?.(persistedItems);

    } catch (error) {
      // Handle upload error
      job.status = 'error';
      job.error = error;

      // Move to completed uploads
      this.completedUploads.set(id, job);

      // Remove from active uploads
      this.activeUploads.delete(id);

      // Notify listeners of error
      this.notifyListeners();

      // Invoke error callback
      job.onError?.(error);
    }

    // Process next job
    this.processQueue();
  }

  // Upload a single file with progress
  uploadFile(storageRef, file, onProgress, metadata) {

    // Return a promise that resolves on upload completion
    return new Promise((resolve, reject) => {

      // Start the upload task
      const uploadTask = uploadBytesResumable(storageRef, file, metadata);

      // Monitor upload state changes
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (typeof onProgress === 'function') {
            onProgress(progress);
          }
        },
        (error) => reject(error),
        async () => {
          resolve(uploadTask.snapshot.ref.fullPath);
        }
      );
    });
  }

  // Save contributions to database (extracted from saveCurrentChildren)
  async saveContributions(items, nodeKey, user, now) {

    // Get parent node context
    const parentDoc = await getDoc(doc(db, collections.nodes, nodeKey));
    const parentNode = parentDoc.data();
    const nodeWrites = [];
    const securedPayloads = [];

    // Prepare each contribution node
    items.forEach(item => {
      const childNode = {
        ...item,
        key: item.key,
        title: item.title || "",
        parentTitle: parentNode?.title || '',
        parentType: parentNode?.type || null,
        userKey: user.key,
        user: buildUserSummary(user),
        dateCreated: now,
        dateModified: now,
      };

      let publicNode;
      let securedDoc;

      // Split secured fields
      try {
        ({ publicNode, securedDoc } = splitNodeSecuredFields(childNode));
      } catch (err) {
        console.warn('NodeUploadService.saveContributions: splitNodeSecuredFields failed; writing child without secured split', err);
        publicNode = childNode;
        securedDoc = {};
      }

      // Write public portion of the contribution node
      nodeWrites.push({ key: childNode.key, payload: publicNode });

      // Queue secured payload (if any) for a follow-up write
      if (securedDoc && Object.keys(securedDoc).length > 0) {
        securedPayloads.push({ key: childNode.key, payload: securedDoc });
      }
    });

    // After the main nodes exist, write secured payloads separately so Firestore
    // security rules that check for the parent node's existence will pass.
    await setNodeDocs(nodeWrites, { merge: false });

    for (const row of nodeWrites) {
      if (!row?.key) continue;
      // eslint-disable-next-line no-await-in-loop
      await upsertEdge(nodeKey, row.key, {
        workspaceKey: String(parentNode?.workspaceKey || '').trim() || null,
      });
    }

    // After the main nodes exist, write secured payloads separately so Firestore
    // security rules that check for the parent node's existence will pass.

    // Write secured payloads separately after main nodes exist
    if (securedPayloads.length > 0) {
      try {
        await setNodeSecuredDocs(securedPayloads, { merge: true, docId: 'main' });
      } catch (securedErr) {
        console.warn('NodeUploadService.saveContributions: failed to write one or more secured payloads for children', securedErr);
      }
    }

    // Touch parent dateModified and record activity
    if (items.length > 0) {
      try {
        await patchNode(nodeKey, { dateModified: now });

      } catch (summaryError) {
        console.warn('NodeUploadService: Failed to update parent after contribution', nodeKey, summaryError);
      }
    }

    // Log contribution engagement for notifications
    const engagementKey = generateKey();
    const engagement = {
      key: engagementKey,
      userKey: user.key,
      user: buildUserSummary(user),
      itemKey: nodeKey,
      itemSummary: parentNode,
      targetKey: parentNode.userKey,
      type: EngagementType.CONTRIBUTION_MADE,
      dateCreated: now
    };
    await createEngagement({ key: engagementKey, engagement });
  }

  // Cancel an upload
  cancelUpload(uploadId) {
    const job = this.activeUploads.get(uploadId);
    if (job) {
      job.status = 'cancelled';
      this.completedUploads.set(uploadId, job);
      this.activeUploads.delete(uploadId);
      this.notifyListeners();
    }
  }

  // Get current status
  getStatus() {
    // Auto-cleanup old completed uploads (older than 30 seconds)
    const now = Date.now();
    const cutoffTime = now - (30 * 1000); // 30 seconds ago
    
    // Clean up old completed uploads
    for (const [id, upload] of this.completedUploads) {
      if (upload.completedAt && upload.completedAt < cutoffTime) {
        this.completedUploads.delete(id);
      }
    }

    // Return current status
    return {
      activeUploads: Array.from(this.activeUploads.values()),
      completedUploads: Array.from(this.completedUploads.values()),
      queueLength: this.uploadQueue.length
    };
  }
}

// Create singleton instance
export const uploadService = new NodeUploadService();